import { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Stack,
  Group,
  Checkbox,
  Text,
  Divider,
  Badge,
  ScrollArea,
  Loader,
  Center,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  useRoles,
  useUserRoles,
  useAssignRole,
  useRevokeRole,
} from '@/models/adminUsers';
import type { User, Role } from '@/types/admin';

interface RoleAssignmentModalProps {
  opened: boolean;
  onClose: () => void;
  user: User | null;
}

export function RoleAssignmentModal({
  opened,
  onClose,
  user,
}: RoleAssignmentModalProps) {
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<number>>(
    new Set(),
  );
  const [initialRoleIds, setInitialRoleIds] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const { data: rolesData, isLoading: rolesLoading } = useRoles();
  const { data: userRolesData, isLoading: userRolesLoading } = useUserRoles(
    user?.id,
  );

  const assignMutation = useAssignRole();
  const revokeMutation = useRevokeRole();

  // Initialize selected roles when data loads
  useEffect(() => {
    if (userRolesData?.roles) {
      const roleIds = new Set(userRolesData.roles.map((r: any) => r.id));
      setSelectedRoleIds(roleIds);
      setInitialRoleIds(roleIds);
    }
  }, [userRolesData, opened]);

  const handleToggleRole = (roleId: number) => {
    const newSelected = new Set(selectedRoleIds);
    if (newSelected.has(roleId)) {
      newSelected.delete(roleId);
    } else {
      newSelected.add(roleId);
    }
    setSelectedRoleIds(newSelected);
  };

  const handleSave = async () => {
    if (!user) return;

    const toAssign = Array.from(selectedRoleIds).filter(
      (id) => !initialRoleIds.has(id),
    );
    const toRevoke = Array.from(initialRoleIds).filter(
      (id) => !selectedRoleIds.has(id),
    );

    setIsSaving(true);

    try {
      // Assign new roles
      for (const roleId of toAssign) {
        await assignMutation.mutateAsync({ userId: user.id, roleId });
      }

      // Revoke removed roles
      for (const roleId of toRevoke) {
        await revokeMutation.mutateAsync({ userId: user.id, roleId });
      }

      notifications.show({
        title: 'Success',
        message: 'User roles updated successfully',
        color: 'green',
      });

      onClose();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error?.message || 'Failed to update user roles',
        color: 'red',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedRoleIds(new Set());
    setInitialRoleIds(new Set());
    onClose();
  };

  const isLoading = rolesLoading || userRolesLoading;
  const hasChanges =
    selectedRoleIds.size !== initialRoleIds.size ||
    Array.from(selectedRoleIds).some((id) => !initialRoleIds.has(id));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Manage User Roles"
      size="md"
    >
      <Stack gap="md">
        {/* User Info */}
        {user && (
          <div>
            <Text size="sm" fw={500}>
              {user.info.name}
            </Text>
            <Text size="xs" c="dimmed">
              {user.email}
            </Text>
          </div>
        )}

        <Divider />

        {/* Roles List */}
        {isLoading ? (
          <Center p="xl">
            <Loader size="sm" />
          </Center>
        ) : (
          <ScrollArea style={{ maxHeight: 400 }}>
            <Stack gap="xs">
              {rolesData?.roles.map((role: Role) => (
                <Checkbox
                  key={role.id}
                  checked={selectedRoleIds.has(role.id)}
                  onChange={() => handleToggleRole(role.id)}
                  label={
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        {role.name}
                      </Text>
                      <Badge size="xs" variant="light">
                        {role.permissions?.length || 0} perms
                      </Badge>
                    </Group>
                  }
                  description={role.description}
                />
              ))}
            </Stack>
          </ScrollArea>
        )}

        {/* Selected Roles Preview */}
        {selectedRoleIds.size > 0 && (
          <>
            <Divider />
            <div>
              <Text size="sm" fw={500} mb="xs">
                Selected Roles: {selectedRoleIds.size}
              </Text>
              <Group gap="xs">
                {rolesData?.roles
                  .filter((role: Role) => selectedRoleIds.has(role.id))
                  .map((role: Role) => (
                    <Badge key={role.id} variant="light">
                      {role.name}
                    </Badge>
                  ))}
              </Group>
            </div>
          </>
        )}

        {/* Actions */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={isSaving}
            disabled={!hasChanges}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
